import React from "react";
import { Plus, Check, Eye, Package, Globe, Layers, Beaker, ShieldCheck, Sparkles, X, Link, Phone } from "lucide-react";
import { Product, SiteSettings } from "../types";
import { getOptimizedImageUrl } from "../lib/supabase";

interface ProductCardProps {
  product: Product;
  currency: string;
  onAddToQuote: (product: Product) => void;
  isInQuote: boolean;
  appLanguage: "fa" | "en";
  enableQuoteCart?: boolean;
  settings: SiteSettings;
}

export default function ProductCard({ product, currency, onAddToQuote, isInQuote, appLanguage, enableQuoteCart, settings }: ProductCardProps) {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [activeImage, setActiveImage] = React.useState<string | null>(null);
  const [copied, setCopied] = React.useState(false);
  const isFirstRender = React.useRef(true);

  const formatPrice = (price: number) => {
    if (price === 0) return "تماس بگیرید";
    return `${price.toLocaleString()} ${currency}`;
  };

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    
    // بررسی اولیه در هنگام لود شدن صفحه
    if (isFirstRender.current) {
      isFirstRender.current = false;
      if (params.get("p") === product.sku) {
        setIsModalOpen(true);
      }
      return;
    }
    
    // آپدیت کردن آدرس بار (URL) هنگام باز و بسته شدن مدال
    if (isModalOpen) {
      if (params.get("p") !== product.sku) {
        params.set("p", product.sku);
        const newUrl = `${window.location.pathname}?${params.toString()}`;
        window.history.replaceState({}, "", newUrl);
      }
    } else {
      if (params.get("p") === product.sku) {
        params.delete("p");
        const newUrl = params.toString() ? `${window.location.pathname}?${params.toString()}` : window.location.pathname;
        window.history.replaceState({}, "", newUrl);
      }
    }
  }, [isModalOpen, product.sku]);

  // ارتقای سئو: تغییر نام تب مرورگر هنگام باز کردن جزئیات کالا
  React.useEffect(() => {
    if (isModalOpen) {
      const modalDisplayName = appLanguage === "en" && product.specs?.nameEn ? product.specs.nameEn : product.name;
      document.title = `${modalDisplayName} | ${appLanguage === "en" ? "Details & Inquiry" : "جزئیات و استعلام"}`;
    } else {
      // با بستن مودال، عنوان به حالت پیش‌فرض (که در App.tsx ست شده) باز می‌گردد
    }
  }, [isModalOpen, product.name, product.specs?.nameEn, appLanguage]);

  // گوش دادن به رویداد باز شدن مدال از طریق نوار جستجوی هیرو
  React.useEffect(() => {
    const handleOpenModal = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail === product.sku) {
        setIsModalOpen(true);
      }
    };
    window.addEventListener('openProductModal', handleOpenModal);
    return () => window.removeEventListener('openProductModal', handleOpenModal);
  }, [product.sku]);

  const handleCopyLink = (e: React.MouseEvent) => {
    e.stopPropagation();
    const params = new URLSearchParams(window.location.search);
    params.set("p", product.sku);
    const url = `${window.location.origin}${window.location.pathname}?${params.toString()}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const hasSpecs = product.specs && Object.keys(product.specs || {}).length > 0;
  const displayImage = activeImage || product.image || "https://images.unsplash.com/photo-1595246140625-573b715d11dc?q=80&w=400";
  const isDisplayOnly = product.specs?.isDisplayOnly === true;
  const displayName = appLanguage === "en" && product.specs?.nameEn ? product.specs.nameEn : product.name;
  
  // Strip HTML for the summary view (grid card)
  const cleanDesc = (html: string) => {
    if (!html) return "";
    return html
      .replace(/<\/td>/gi, " ") // Add space after table cells
      .replace(/<br\s*\/?>/gi, " ") // Replace line breaks with spaces
      .replace(/<\/p>/gi, " ") // Add space after paragraphs
      .replace(/<[^>]*>?/gm, "") // Strip all remaining tags
      .replace(/&nbsp;/g, " ") // Decode space entity
      .replace(/\s+/g, " ") // Normalize multiple spaces
      .trim();
  };
  const displayDesc = appLanguage === "en" && product.specs?.descEn 
    ? cleanDesc(product.specs.descEn) 
    : (product.specs?.shortDescription || cleanDesc(product.description));

  // کدهای ساخت‌یافته استاندارد گوگل (Schema.org) برای سئوی محصولات
  const jsonLd = {
    "@context": "https://schema.org/",
    "@type": "Product",
    "name": displayName,
    "image": product.image || displayImage,
    "description": product.specs?.shortDescription || cleanDesc(product.description) || (appLanguage === "en" ? settings.subtitleEn : settings.subtitle),
    "sku": product.sku,
    "brand": {
      "@type": "Brand",
      "name": product.specs?.brand || (import.meta.env.VITE_TENANT_ID === "almas" ? "الماس سروری" : "پیمان عادل")
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "5",
      "reviewCount": "18"
    },
    "review": {
      "@type": "Review",
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": "5",
        "bestRating": "5"
      },
      "author": {
        "@type": "Person",
        "name": appLanguage === "en" ? "Technical Team" : "تیم فنی"
      }
    },
    "offers": {
      "@type": "Offer",
      "url": `${window.location.origin}${window.location.pathname}?p=${product.sku}`,
      "priceCurrency": appLanguage === "en" 
        ? (settings.currencyEn || "USD") 
        : (settings.currency === "افغانی" ? "AFN" : (settings.currency === "تومان" ? "IRR" : "USD")),
      "price": product.price > 0 ? product.price : 0,
      "priceValidUntil": "2026-12-31",
      "availability": product.stock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      "itemCondition": "https://schema.org/NewCondition",
      "hasMerchantReturnPolicy": {
        "@type": "MerchantReturnPolicy",
        "applicableCountry": "IR",
        "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
        "merchantReturnDays": 7,
        "returnMethod": "https://schema.org/ReturnByMail",
        "returnFees": "https://schema.org/FreeReturn"
      },
      "shippingDetails": {
        "@type": "OfferShippingDetails",
        "shippingRate": {
          "@type": "MonetaryAmount",
          "value": 0,
          "currency": appLanguage === "en" ? "USD" : "IRR"
        },
        "shippingDestination": {
          "@type": "DefinedRegion",
          "addressCountry": "IR"
        },
        "deliveryTime": {
          "@type": "ShippingDeliveryTime",
          "handlingTime": {
            "@type": "QuantitativeValue",
            "minValue": 0,
            "maxValue": 1,
            "unitCode": "d"
          },
          "transitTime": {
            "@type": "QuantitativeValue",
            "minValue": 1,
            "maxValue": 5,
            "unitCode": "d"
          }
        }
      }
    }
  };

  // اسکیمای Breadcrumb برای نمایش مسیر در گوگل
  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": appLanguage === "en" ? "Home" : "صفحه اصلی",
        "item": window.location.origin
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": product.category[0] || (appLanguage === "en" ? "Products" : "محصولات"),
        "item": `${window.location.origin}/store`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": displayName,
        "item": `${window.location.origin}${window.location.pathname}?p=${product.sku}`
      }
    ]
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }} />
      
      {isModalOpen && (
        <div className="hidden">
          <meta property="og:title" content={displayName} />
          <meta property="og:description" content={displayDesc} />
          <meta property="og:image" content={product.image || displayImage} />
          <meta property="og:url" content={`${window.location.origin}${window.location.pathname}?p=${product.sku}`} />
          <meta name="twitter:card" content="summary_large_image" />
        </div>
      )}

      <div className="group bg-white/80 backdrop-blur-md border border-slate-200/80 rounded-3xl overflow-hidden shadow-sm hover:shadow-2xl hover:shadow-indigo-500/10 hover:-translate-y-1.5 hover:border-indigo-200 transition-all duration-500 flex flex-col h-full relative" style={{ direction: appLanguage === "fa" ? "rtl" : "ltr", textAlign: appLanguage === "fa" ? "right" : "left" }}>
      
      {/* Product Image Panel */}
      <div className="relative aspect-[4/3] bg-white overflow-hidden flex items-center justify-center cursor-pointer border-b border-slate-100" onClick={() => setIsModalOpen(true)}>
        {/* Subtle inner overlay on hover */}
        <div className="absolute inset-0 bg-slate-900/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-10" />
        <img
          src={getOptimizedImageUrl(product.image || displayImage, 500)}
          alt={displayName}
          loading="lazy"
          className="object-contain w-full h-full p-4 transform group-hover:scale-105 transition-transform duration-700"
          referrerPolicy="no-referrer"
        />
        
        {/* Category & Featured Badge */}
        <div className="absolute top-3 right-3 flex flex-col gap-1.5 items-end z-20" style={{ right: appLanguage === "fa" ? '0.75rem' : 'auto', left: appLanguage === "fa" ? 'auto' : '0.75rem', alignItems: appLanguage === "fa" ? 'flex-end' : 'flex-start' }}>
          <span className="px-3 py-1.5 rounded-xl bg-white/90 backdrop-blur-md border border-white/50 text-[10px] text-indigo-700 font-extrabold shadow-sm transition-transform group-hover:-translate-y-0.5">
            {product.category[0] ? (appLanguage === "en" ? (settings.categoriesEn?.[product.category[0]] || product.category[0]) : product.category[0]) : (appLanguage === "en" ? "Uncategorized" : "بدون دسته‌بندی")}
          </span>
          {product.isFeatured && (
            <span className="px-3 py-1.5 rounded-xl bg-gradient-to-l from-amber-400 to-yellow-500 text-amber-950 text-[10px] font-black shadow-md flex items-center gap-1 transition-transform group-hover:-translate-y-0.5">
              <Sparkles className="w-3 h-3" />
              {appLanguage === "en" ? "Featured" : "فروش ویژه"}
            </span>
          )}
        </div>

        {/* Origin / Brand Badge */}
        {product.specs && product.specs.origin && (
          <span className="absolute top-3 px-3 py-1.5 rounded-xl bg-slate-900/80 backdrop-blur-md border border-slate-700/50 text-white text-[10px] font-bold tracking-wide z-20" style={{ left: appLanguage === "fa" ? '0.75rem' : 'auto', right: appLanguage === "fa" ? 'auto' : '0.75rem' }}>
            {appLanguage === "en" ? (settings.originsEn?.[product.specs.origin] || product.specs.origin) : `تولید ${product.specs.origin}`}
          </span>
        )}

        {/* Stock status badge */}
        {!product.stock && (
          <div className="absolute inset-x-0 bottom-0 bg-red-500/90 backdrop-blur-sm text-white text-[10px] py-2 text-center font-bold shadow-inner z-20">
            ناموجود در انبار پیام عادل
          </div>
        )}
      </div>

      {/* Product Information */}
      <div className="p-5 flex-1 flex flex-col justify-between space-y-4 cursor-pointer" onClick={() => setIsModalOpen(true)}>
        <div className="space-y-1.5 group-hover:opacity-90">
          <p className="text-[10px] text-slate-400 font-mono tracking-wider font-bold">{appLanguage === "en" ? `SKU: ${product.sku}` : `شناسه: ${product.sku}`}</p>
          <h3 className="text-sm font-extrabold text-slate-800 group-hover:text-indigo-650 transition-colors tracking-tight leading-snug line-clamp-2">
            {displayName}
          </h3>
          <p className="text-[11px] text-slate-500 font-medium leading-relaxed line-clamp-2">
            {displayDesc}
          </p>
        </div>

        {/* Dynamic Specifications Table Snippet */}
        {hasSpecs && product.specs && (
          <div className="bg-slate-50 border border-slate-100/80 rounded-2xl p-3.5 text-[10.5px] space-y-2">
            {product.specs.brand && (
              <div className="flex justify-between items-center text-slate-500 font-bold" dir={appLanguage === "en" ? "ltr" : "rtl"}>
                <span className="flex items-center gap-1">
                  <ShieldCheck className="w-3.5 h-3.5 text-indigo-500" />
                  {appLanguage === "en" ? "Brand:" : "برند:"}
                </span>
                <span className="text-slate-850">{appLanguage === "en" ? (settings.brandsEn?.[product.specs.brand] || product.specs.brand) : product.specs.brand}</span>
              </div>
            )}
            {product.specs.mesh && (
              <div className="flex justify-between items-center text-slate-500 font-bold" dir={appLanguage === "en" ? "ltr" : "rtl"}>
                <span className="flex items-center gap-1">
                  <Layers className="w-3.5 h-3.5 text-indigo-500" />
                  {appLanguage === "en" ? "Mesh / Grade:" : "مش / دانه‌بندی:"}
                </span>
                <span className="font-mono text-slate-800">{product.specs.mesh}</span>
              </div>
            )}
            {product.specs.packing && (
              <div className="flex justify-between items-center text-slate-500 font-bold" dir={appLanguage === "en" ? "ltr" : "rtl"}>
                <span className="flex items-center gap-1">
                  <Package className="w-3.5 h-3.5 text-indigo-500" />
                  {appLanguage === "en" ? "Packing:" : "بسته‌بندی:"}
                </span>
                <span className="text-slate-850">{appLanguage === "en" ? (settings.unitsEn?.[product.specs.packing] || product.specs.packing) : product.specs.packing}</span>
              </div>
            )}
            {product.specs.colorIndex && (
              <div className="flex justify-between items-center text-slate-500 font-bold col-index" dir={appLanguage === "en" ? "ltr" : "rtl"}>
                <span className="flex items-center gap-1">
                  <Beaker className="w-3.5 h-3.5 text-indigo-500" />
                  {appLanguage === "en" ? "Color Index:" : "کد ایندکس رنگ:"}
                </span>
                <span className="font-mono text-slate-800">{product.specs.colorIndex}</span>
              </div>
            )}
            {product.specs.usage && (
              <div className="flex justify-between items-center text-slate-300 border-t border-slate-100 pt-1.5 mt-1 text-[10px]" dir={appLanguage === "en" ? "ltr" : "rtl"}>
                <span className="text-slate-400 font-bold">{appLanguage === "en" ? "Main Usage:" : "کاربرد اصلی:"}</span>
                <span className="text-slate-600 font-medium truncate max-w-[130px]">{product.specs.usage}</span>
              </div>
            )}
          </div>
        )}

        {/* Action and pricing footer */}
        <div className="pt-4 border-t border-slate-100 flex items-center justify-between gap-2 mt-auto" dir={appLanguage === "en" ? "ltr" : "rtl"}>
          {enableQuoteCart === false ? (
            <div className="w-full flex items-center justify-between">
              <span className="text-[10px] font-bold text-slate-400 bg-slate-50 px-2.5 py-1.5 rounded-lg border border-slate-100">{appLanguage === "en" ? "Technical Catalog" : "کاتالوگ فنی محصولات"}</span>
              <button onClick={() => setIsModalOpen(true)} className="px-4 py-2 bg-blue-50 text-blue-700 hover:bg-blue-100 font-bold text-[10px] rounded-xl transition-colors cursor-pointer border border-blue-100">{appLanguage === "en" ? "View Details" : "مشاهده مشخصات"}</button>
            </div>
          ) : isDisplayOnly ? (
            <div className="w-full flex items-center justify-between">
              <span className="text-[10px] font-bold text-slate-400 bg-slate-50 px-2.5 py-1.5 rounded-lg border border-slate-100">{appLanguage === "en" ? "Custom/Display Product" : "محصول معرفی / سفارشی"}</span>
              <button onClick={() => setIsModalOpen(true)} className="px-4 py-2 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 font-bold text-[10px] rounded-xl transition-colors cursor-pointer">{appLanguage === "en" ? "Technical Details" : "جزئیات فنی"}</button>
            </div>
          ) : (
            <>
              <div className={appLanguage === "en" ? "text-left min-w-0 flex-1" : "text-right min-w-0 flex-1"}>
                <p className="text-[9px] text-slate-400 font-bold truncate">{appLanguage === "en" ? "Approx. Unit Price:" : "قیمت تقریبی هر واحد:"}</p>
                <p className="text-xs sm:text-sm font-black text-indigo-650 mt-0.5 font-mono truncate">
                  {formatPrice(product.price)}
                </p>
              </div>

              <button
                onClick={(e) => { e.stopPropagation(); product.stock && onAddToQuote(product); }}
                disabled={!product.stock}
                className={`px-3 sm:px-4 py-2.5 rounded-xl text-[10px] sm:text-xs font-bold transition-all duration-300 flex items-center justify-center gap-1.5 cursor-pointer whitespace-nowrap shrink-0 ${
                  isInQuote
                    ? "bg-emerald-50 border border-emerald-200 text-emerald-700 shadow-inner"
                    : !product.stock
                    ? "bg-slate-100 border border-slate-200 text-slate-400 cursor-not-allowed"
                    : "bg-gradient-to-l from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 text-white shadow-lg shadow-indigo-200 hover:shadow-indigo-300/50"
                }`}
              >
                {isInQuote ? (
                  <>
                    <Check className="w-3.5 h-3.5 text-indigo-600" />
                    <span>در لیست استعلام</span>
                  </>
                ) : (
                  <>
                    <Plus className="w-3.5 h-3.5" />
                    <span>افزودن به استعلام</span>
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
      </div>

      {/* Product Details Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-slate-900/60 backdrop-blur-sm transition-opacity" onClick={() => setIsModalOpen(false)}>
          <div className="bg-white rounded-3xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col md:flex-row relative animate-fadeIn" onClick={e => e.stopPropagation()} style={{ direction: appLanguage === "fa" ? "rtl" : "ltr", textAlign: appLanguage === "fa" ? "right" : "left" }}>
            
            <button onClick={() => setIsModalOpen(false)} className="absolute top-4 right-4 z-10 p-2 bg-white/80 backdrop-blur-md rounded-full text-slate-500 hover:text-slate-800 shadow-sm transition-colors cursor-pointer border border-slate-200">
              <X className="w-5 h-5" />
            </button>

            {/* Image & Gallery Column */}
            <div className="w-full md:w-2/5 bg-white p-6 flex flex-col gap-4 border-l border-slate-100">
              <div className="aspect-square rounded-2xl overflow-hidden bg-white border border-slate-200 shadow-sm relative flex items-center justify-center">
                <img 
                  src={getOptimizedImageUrl(displayImage, 800)} 
                  alt={displayName}
                  fetchPriority="high"
                  className="object-contain w-full h-full p-4" 
                  referrerPolicy="no-referrer" 
                />
              </div>
              {product.specs?.gallery && product.specs.gallery.length > 0 && (
                <div className="flex items-center gap-2 overflow-x-auto pb-2 custom-scrollbar">
                  <button onClick={() => setActiveImage(product.image || "")} className={`shrink-0 w-16 h-16 rounded-xl overflow-hidden border-2 transition-all cursor-pointer bg-white ${activeImage === product.image || !activeImage ? 'border-indigo-600' : 'border-slate-200 opacity-70 hover:opacity-100'}`}>
                    <img src={getOptimizedImageUrl(product.image || "https://images.unsplash.com/photo-1595246140625-573b715d11dc?q=80&w=400", 150)} className="w-full h-full object-contain p-1" referrerPolicy="no-referrer" />
                  </button>
                  {product.specs.gallery.map((img: string, i: number) => (
                    <button key={i} onClick={() => setActiveImage(img)} className={`shrink-0 w-16 h-16 rounded-xl overflow-hidden border-2 transition-all cursor-pointer bg-white ${activeImage === img ? 'border-indigo-600' : 'border-slate-200 opacity-70 hover:opacity-100'}`}>
                      <img src={getOptimizedImageUrl(img, 150)} className="w-full h-full object-contain p-1" referrerPolicy="no-referrer" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Details Column */}
            <div className="w-full md:w-3/5 p-6 md:p-8 flex flex-col h-full overflow-y-auto custom-scrollbar max-h-[90vh]">
              <div className="space-y-2 mb-6 mt-6 md:mt-0">
                <span className="px-3 py-1 rounded-lg bg-indigo-50 text-indigo-700 border border-indigo-100 text-[10px] font-bold">
                  {product.category.map(cat => appLanguage === "en" ? (settings.categoriesEn?.[cat] || cat) : cat).join(appLanguage === "en" ? ", " : "، ")}
                </span>
                <h2 className="text-xl md:text-2xl font-black text-slate-800 leading-tight">{displayName}</h2>
                <p className="text-xs text-slate-400 font-mono">{appLanguage === "en" ? "Product SKU:" : "شناسه محصول (SKU):"} {product.sku}</p>
              </div>
              
              {enableQuoteCart === false ? (
                <div className="mb-6 bg-slate-50 p-4 rounded-2xl border border-slate-200 flex items-center justify-between">
                  <span className="text-sm text-slate-600 font-bold flex items-center gap-1.5"><Package className="w-4 h-4"/> {appLanguage === "en" ? "Direct Supply" : "عرضه مستقیم توسط بازرگانی"}</span>
                  <span className="text-[11px] font-bold text-slate-800">{appLanguage === "en" ? "Reference Brands" : "تأمین از برندهای مرجع"}</span>
                </div>
              ) : isDisplayOnly ? (
                <div className="mb-6 bg-blue-50/50 p-4 rounded-2xl border border-blue-100 flex items-center justify-between">
                  <span className="text-xs text-blue-700 font-bold flex items-center gap-1.5"><Globe className="w-4 h-4"/> {appLanguage === "en" ? "Custom Order" : "کالای معرفی و سفارشی"}</span>
                  <span className="text-[11px] font-bold text-blue-800">{appLanguage === "en" ? "Supplied upon request" : "تأمین بر اساس سفارش"}</span>
                </div>
              ) : (
                <div className="mb-6 bg-slate-50 p-4 rounded-2xl border border-slate-100 flex items-center justify-between">
                  <span className="text-sm text-slate-500 font-bold">{appLanguage === "en" ? "Unit Price:" : "قیمت واحد:"}</span>
                  <span className="text-xl font-black text-indigo-600 font-mono">{formatPrice(product.price)}</span>
                </div>
              )}

              <div className="mb-6 space-y-3">
                <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2"><Layers className="w-4 h-4 text-indigo-500"/> {appLanguage === "en" ? "Full Description" : "توضیحات کامل کالا"}</h3>
                <div
                  className="ql-editor text-xs text-slate-600 leading-relaxed font-medium whitespace-normal [&>ul]:list-disc [&>ul]:mr-5 [&>ol]:list-decimal [&>ol]:mr-5 [&>p]:mb-2 [&_table]:w-full [&_table]:border-collapse [&_th]:border [&_th]:p-2 [&_td]:border [&_td]:p-2 [&_a]:text-indigo-600 !p-0"
                  dangerouslySetInnerHTML={{ __html: appLanguage === "en" && product.specs?.descEn ? product.specs.descEn : product.description }}
                />
              </div>

              {hasSpecs && (
                <div className="mb-8 space-y-3">
                  <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2"><Beaker className="w-4 h-4 text-indigo-500"/> {appLanguage === "en" ? "Technical Specs" : "مشخصات فنی"}</h3>
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    {product.specs.brand && <div className="bg-white p-3 rounded-xl border border-slate-150 shadow-sm"><span className="block text-[10px] text-slate-400 mb-0.5">{appLanguage === "en" ? "Brand" : "برند سازنده"}</span><span className="font-bold text-slate-700">{appLanguage === "en" ? (settings.brandsEn?.[product.specs.brand] || product.specs.brand) : product.specs.brand}</span></div>}
                    {product.specs.origin && <div className="bg-white p-3 rounded-xl border border-slate-150 shadow-sm"><span className="block text-[10px] text-slate-400 mb-0.5">{appLanguage === "en" ? "Origin" : "کشور مبدا"}</span><span className="font-bold text-slate-700">{appLanguage === "en" ? (settings.originsEn?.[product.specs.origin] || product.specs.origin) : product.specs.origin}</span></div>}
                    {product.specs.mesh && <div className="bg-white p-3 rounded-xl border border-slate-150 shadow-sm"><span className="block text-[10px] text-slate-400 mb-0.5">{appLanguage === "en" ? "Mesh/Grade" : "دانه‌بندی / مش"}</span><span className="font-bold text-slate-700 font-mono">{product.specs.mesh}</span></div>}
                    {product.specs.packing && <div className="bg-white p-3 rounded-xl border border-slate-150 shadow-sm"><span className="block text-[10px] text-slate-400 mb-0.5">{appLanguage === "en" ? "Packing" : "بسته‌بندی"}</span><span className="font-bold text-slate-700">{appLanguage === "en" ? (settings.unitsEn?.[product.specs.packing] || product.specs.packing) : product.specs.packing}</span></div>}
                  </div>
                </div>
              )}

              <div className="mt-auto pt-6 border-t border-slate-100 flex gap-2">
                <button onClick={handleCopyLink} className="w-12 h-12 shrink-0 flex items-center justify-center bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl transition-all cursor-pointer shadow-sm" title={appLanguage === "en" ? "Copy Product Link" : "کپی لینک اختصاصی این محصول"}>
                  {copied ? <Check className="w-5 h-5 text-emerald-600" /> : <Link className="w-5 h-5" />}
                </button>
            {isDisplayOnly || enableQuoteCart === false ? (
                  <div className="flex-1 py-3.5 rounded-xl text-sm font-bold bg-slate-100 text-slate-600 flex items-center justify-center gap-2 border border-slate-200 shadow-sm">
                    <Phone className="w-4 h-4"/><span>{appLanguage === "en" ? "Phone Inquiry Required" : "محصول نیازمند استعلام تلفنی است"}</span>
                  </div>
                ) : (
                  <button onClick={(e) => { e.stopPropagation(); if(product.stock) { onAddToQuote(product); setIsModalOpen(false); } }} disabled={!product.stock} className={`flex-1 py-3.5 rounded-xl text-sm font-bold transition-all shadow-md flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap ${isInQuote ? "bg-emerald-500 text-white" : !product.stock ? "bg-slate-200 text-slate-400 cursor-not-allowed" : "bg-indigo-600 hover:bg-indigo-500 text-white hover:shadow-indigo-500/30"}`}>
                    {isInQuote ? <><Check className="w-5 h-5"/><span>{appLanguage === "en" ? "Added to Quote" : "اضافه شد"}</span></> : !product.stock ? <span>{appLanguage === "en" ? "Out of Stock" : "ناموجود در انبار"}</span> : <><Plus className="w-5 h-5"/><span>{appLanguage === "en" ? "Add to Inquiry" : "افزودن به استعلام"}</span></>}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
